import { useEffect, useRef } from 'react';
import Modal from './Modal';
import { createPortal } from 'react-dom';
import { useIsMobile } from '../utils/useIsMobile';
import Icon from './Icon';
import { useSheetDrag } from '../utils/useSheetDrag';

// "Not built yet, and we want to know if you want it."
//
// Behind the לצפייה footer on רכש מול יעדי תקציב, which has no report page
// behind it. The card's footer was a deliberately inert <span> so it would
// never imply a click; the owner's decision is to make it clickable, count the
// clicks (EVENTS.BUDGET_REPORT_INTEREST) and build the report only if people
// actually reach for it. So this sheet is a measuring instrument that happens
// to be polite — the copy has to leave someone glad they tapped, or the number
// it produces is measuring annoyance instead of demand.
//
// HEART, not a construction sign or a warning triangle: the same glyph
// AmberCallout uses, for the same reason recorded there — the owner wants
// "something worth finishing" to read casual, not like an incident. It is the
// house motif for a gap in the product, so a gap announced any other way would
// look like a different product.
//
// Bottom sheet on a phone, centred dialog on a desktop — the split the payment
// panel already makes (.payment-sheet / .payment-popover). A bottom sheet is
// thumb-reachable and is what the platform has trained people to dismiss; the
// same thing pinned to the bottom of a 1400px window just looks lost.
export default function ComingSoonSheet({ open, onClose, title, children }) {
  const mobile = useIsMobile(720);
  const sheetRef = useRef(null);
  const backdropRef = useRef(null);
  useSheetDrag(sheetRef, onClose, { enabled: open && mobile, backdropRef });

  // Body scroll lock — MOBILE ONLY. On desktop this sheet renders through
  // <Modal>, which already locks (and, critically, restores) body scroll
  // itself via its own instance-counted lock. Locking here too used to run
  // BOTH: Modal's effect fires first (it is the child), capturing the true
  // pre-lock overflow and setting 'hidden'; this effect then ran second and
  // captured Modal's already-'hidden' value as "the previous state" — so on
  // close, Modal correctly restored scrolling and this effect's cleanup then
  // immediately clobbered it back to 'hidden', leaving the page unscrollable
  // until a full reload. The bottom-sheet (mobile) path has no such wrapper,
  // so it still needs its own lock.
  useEffect(() => {
    if (!open || !mobile) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [open, mobile]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  const body = (
    <>
      {/* Two hearts at two sizes rather than one: the big one is the
          illustration this sheet has instead of a graphic, the small one in
          the closing line signs it off. */}
      <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 14 }}>
        <span style={{
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          width: 64, height: 64, borderRadius: '50%', background: '#fef7ec', color: '#B45309',
        }}>
          <Icon name="heart" size={30} />
        </span>
      </div>
      <h2 style={{
        margin: '0 0 8px', fontSize: 18, fontWeight: 700,
        color: '#1a1a2e', textAlign: 'center',
      }}>{title}</h2>
      <div style={{
        fontSize: 14.5, lineHeight: 1.6, color: '#475569', textAlign: 'center',
      }}>{children}</div>
      <button
        type="button"
        className="btn btn-secondary"
        onClick={onClose}
        style={{ width: '100%', minHeight: 44, justifyContent: 'center', marginTop: 18 }}
      >
        סגירה
      </button>
    </>
  );

  if (!mobile) {
    return (
      <Modal onClose={onClose} size={400} ariaLabel={title}>{body}</Modal>
    );
  }

  // Reuses FilterSheet's chrome classes rather than a second set of near-identical
  // ones — same backdrop, radius, shadow, slide-up and z-index (1050/1051, below
  // the date picker's 1100/1101), so the two sheets read as one system.
  return createPortal(
    <>
      <div ref={backdropRef} className="filter-sheet-backdrop" onClick={onClose} aria-hidden />
      <div ref={sheetRef} className="filter-sheet" role="dialog" aria-modal="true" aria-label={title}>
        <div className="filter-sheet-handle" aria-hidden />
        <div className="filter-sheet-body" style={{ padding: '8px 20px 28px' }}>{body}</div>
      </div>
    </>,
    document.body
  );
}
